/* global BigInt */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from "@solana/spl-token";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { Pencil, UserPlus, UserCheck, Search, BadgeCheck } from "lucide-react";
import bs58 from "bs58";
import { slurpApi, profileCache } from "../../lib/slurpApi";
import { adaptSlurpToken } from "../../lib/adaptToken";
import { fromRawAmount } from "../../lib/solanaSwap";
import { copyToClipboard } from "../GlobalFunctions";
import { IconX, IconGlobe, IconTelegram, IconGithub } from "./icons";
import ProfileFormModal, { gradientFor } from "./ProfileFormModal";
import LaunchCardWithClaim from "./LaunchCardWithClaim";

const fmtUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  if (v >= 1)   return `$${v.toFixed(2)}`;
  if (v > 0)    return `$${v.toPrecision(3)}`;
  return "—";
};

const shortAddr = (a, head = 4, tail = 4) =>
  !a ? "—" : `${a.slice(0, head)}…${a.slice(-tail)}`;

function timeAgo(ms) {
  const s = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function SlurpLoader({ label = "Loading" }) {
  return (
    <div className="pm-slurp-loader" role="status" aria-live="polite">
      <img
        src="/images/png/slurpface (1).png"
        alt="Slurp"
        style={{ width: 64, height: 64 }}
      />
      <span className="pm-slurp-loader__label">{label}</span>
    </div>
  );
}

export default function ProfilePage() {
  const params = useParams();
  const navigate = useNavigate();
  const wallet = useWallet();
  const { connection } = useConnection();

  // URL wallet (route is /profile/:wallet) OR connected wallet on /portfolio
  const urlWallet = params.wallet || null;
  const connectedWallet = wallet.publicKey?.toBase58() || null;
  const owner = urlWallet || connectedWallet;
  const isSelf = !!(connectedWallet && owner && connectedWallet === owner);

  // Seed from cache if we already fetched this wallet's profile (e.g.,
  // welcome modal on first connect, or an earlier visit). Avoids the
  // shortAddr → @username flash on subsequent visits.
  const [profile, setProfile] = useState(() => {
    const cached = profileCache.get(owner);
    return cached === undefined ? null : cached;
  });
  const [profileLoading, setProfileLoading] = useState(false);

  const [data, setData] = useState({ launches: [], holdings: [] });
  const [trades, setTrades] = useState([]);
  const [onChain, setOnChain] = useState({});
  const [onChainChecked, setOnChainChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [tab, setTab] = useState("launched");
  // Once the user clicks Holdings, latch this so the on-chain RPC fires.
  // Avoids burning a getParsedTokenAccountsByOwner on every profile open.
  const [holdingsRequested, setHoldingsRequested] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);
  // Banner shown after returning from the X OAuth flow. "ok" = success,
  // any other string = the error code from /callback. Cleared by user
  // dismissal or after auto-fade.
  const [xVerifyBanner, setXVerifyBanner] = useState(null);

  // Read ?x_verified / ?x_error once on mount, then strip them from the URL
  // so a refresh doesn't re-show the banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get("x_verified");
    const err = params.get("x_error");
    if (!ok && !err) return;
    if (ok) {
      setXVerifyBanner("ok");
      // Drop the cached profile so the refetch below picks up the new
      // x_verified_username from the backend.
      profileCache.invalidate(owner);
    } else if (err) {
      setXVerifyBanner(err);
    }
    // Strip the params so reloading doesn't re-trigger this effect or
    // re-show the banner.
    const url = new URL(window.location.href);
    url.searchParams.delete("x_verified");
    url.searchParams.delete("x_error");
    window.history.replaceState({}, "", url.toString());
    // Auto-dismiss success after 6s; errors stay until the user dismisses
    // so they can actually read the failure code.
    if (ok) {
      const t = setTimeout(() => setXVerifyBanner(null), 6000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow state
  const [followStats, setFollowStats] = useState({ followers: 0, following: 0, viewer_follows: false });
  const [followBusy, setFollowBusy] = useState(false);
  // null = closed; "followers" | "following" = which list to show
  const [followListMode, setFollowListMode] = useState(null);
  const [followListData, setFollowListData] = useState([]);
  const [followListLoading, setFollowListLoading] = useState(false);

  // User search — substring match on usernames, exact match on wallets.
  // Debounced 250ms so we don't fan out a request per keystroke. Results
  // collapse on blur or after a result is clicked.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    let cancelled = false;
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await slurpApi.searchProfiles(q, 10);
        if (cancelled) return;
        setSearchResults(Array.isArray(r?.profiles) ? r.profiles : []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery]);

  const openFollowList = useCallback(async (mode) => {
    if (!owner) return;
    setFollowListMode(mode);
    setFollowListLoading(true);
    setFollowListData([]);
    try {
      const r = mode === "followers"
        ? await slurpApi.getFollowers(owner)
        : await slurpApi.getFollowing(owner);
      setFollowListData(Array.isArray(r?.list) ? r.list : []);
    } catch (err) {
      console.warn("[profile] follow list fetch failed:", err?.message || err);
    } finally {
      setFollowListLoading(false);
    }
  }, [owner]);

  // Fetch profile
  useEffect(() => {
    if (!owner) return;
    let cancelled = false;
    setProfileLoading(true);
    slurpApi.getProfile(owner)
      .then((r) => { if (!cancelled) setProfile(r.profile); })
      .catch(() => { if (!cancelled) setProfile(null); })
      .finally(() => { if (!cancelled) setProfileLoading(false); });
    return () => { cancelled = true; };
  }, [owner]);

  // Fetch follow stats (refetch when viewer changes)
  const refreshFollow = useCallback(async () => {
    if (!owner) return;
    try {
      const r = await slurpApi.getFollowStats(owner, connectedWallet || undefined);
      setFollowStats({
        followers: Number(r?.followers ?? 0),
        following: Number(r?.following ?? 0),
        viewer_follows: !!r?.viewer_follows,
      });
    } catch {
      /* swallow — stats are non-critical */
    }
  }, [owner, connectedWallet]);
  useEffect(() => { refreshFollow(); }, [refreshFollow]);

  const toggleFollow = useCallback(async () => {
    if (!wallet?.publicKey || !wallet?.signMessage || !owner || isSelf) return;
    setFollowBusy(true);
    try {
      const ts = Date.now();
      const message = new TextEncoder().encode(`slurp-profile:${ts}`);
      const sigBytes = await wallet.signMessage(message);
      const sig = bs58.encode(sigBytes);
      const headers = {
        "X-Profile-Address": connectedWallet,
        "X-Profile-Ts": String(ts),
        "X-Profile-Sig": sig,
      };
      if (followStats.viewer_follows) {
        await slurpApi.unfollowProfile(owner, headers);
      } else {
        await slurpApi.followProfile(owner, headers);
      }
      await refreshFollow();
    } catch (err) {
      console.warn("[follow] failed:", err?.message || err);
    } finally {
      setFollowBusy(false);
    }
  }, [wallet, owner, isSelf, connectedWallet, followStats.viewer_follows, refreshFollow]);

  // Fetch portfolio (launches + holdings)
  const refreshPortfolio = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    setFetchError(null);
    try {
      const r = await slurpApi.getPortfolio(owner);
      setData({
        launches: Array.isArray(r?.launches) ? r.launches : [],
        holdings: Array.isArray(r?.holdings) ? r.holdings : [],
      });
    } catch (err) {
      setFetchError(err?.message || "Couldn't load portfolio");
    } finally {
      setLoading(false);
    }
  }, [owner]);

  useEffect(() => { refreshPortfolio(); }, [refreshPortfolio]);

  // Pre-fetch pending claim fees for every launch this wallet owns in
  // ONE backend call instead of letting each LaunchCardWithClaim fire
  // its own RPC reads. Without this, 20+ cards mounting at once each
  // triggered fetchPositionState + fetchPoolState in parallel and
  // exceeded Helius's burst limit (429s in the console). Only fires
  // for the connected wallet on their own profile — visitors viewing
  // someone else's profile don't see the claim widget anyway.
  const [pendingFees, setPendingFees] = useState({});
  useEffect(() => {
    if (!owner || !isSelf) { setPendingFees({}); return; }
    let cancelled = false;
    slurpApi.getPendingFees(owner)
      .then((r) => { if (!cancelled) setPendingFees(r?.pending ?? {}); })
      .catch(() => { if (!cancelled) setPendingFees({}); });
    return () => { cancelled = true; };
  }, [owner, isSelf]);

  // Fetch trade history
  useEffect(() => {
    if (!owner || tab !== "trades") return;
    let cancelled = false;
    setTradesLoading(true);
    // 200 = enough to fill the scroll box on most active wallets while
    // staying under the trades-table 90-day retention floor (where older
    // rows are deleted by the indexer's nightly cleanup, so anything
    // beyond 200 rows in 90 days is rare).
    slurpApi.getWalletTrades(owner, { limit: 200 })
      .then((r) => { if (!cancelled) setTrades(r?.trades ?? []); })
      .catch(() => { if (!cancelled) setTrades([]); })
      .finally(() => { if (!cancelled) setTradesLoading(false); });
    return () => { cancelled = true; };
  }, [owner, tab]);

  // On-chain SPL balances for accurate holdings. Reset the "checked" flag
  // and the request latch when the wallet/owner changes so we re-fetch
  // fresh on-chain truth (only) when the user clicks Holdings.
  useEffect(() => {
    setOnChainChecked(false);
    setOnChain({});
    setHoldingsRequested(false);
  }, [owner, wallet.publicKey]);

  useEffect(() => {
    // Don't fire until the user actually opens Holdings. Saves a backend
    // RPC call per profile-open across every visitor — most just look at
    // Launched and bounce.
    //
    // Server-side fetch (was browser-side getParsedTokenAccountsByOwner)
    // — keeps the heavy RPC call off the user's free-tier Helius key and
    // lets us cache 30s for repeated profile views.
    if (!owner || !holdingsRequested) return;
    let cancelled = false;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";
        const r = await fetch(`${apiBase}/api/portfolio/${owner}/onchain-holdings`);
        if (!r.ok) throw new Error(`onchain-holdings ${r.status}`);
        const { holdings } = await r.json();
        if (cancelled) return;
        setOnChain(holdings || {});
        setOnChainChecked(true);
      } catch (err) {
        if (cancelled) return;
        console.warn("[profile] onchain-holdings fetch failed:", err);
        // Mark checked so the UI stops spinning even on failure; the
        // existing DB-backed holdings list will still render.
        setOnChainChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, holdingsRequested]);

  const launches = useMemo(
    () => data.launches
      .map((raw) => ({ raw, adapted: adaptSlurpToken(raw) }))
      .filter((l) => l.adapted),
    [data.launches]
  );

  const holdings = useMemo(() => {
    // On-chain is truth for everyone. Show loader until it lands so no
    // stale indexer rows flash. Indexer still provides token metadata
    // (name, image, price) but never balance.
    if (!onChainChecked) return null;
    return data.holdings.map((row) => {
      const ui = adaptSlurpToken(row);
      const decimals = Number(row.decimals || 6);
      const rawBalance = onChain[row.mint] ?? "0";
      const balanceWhole = fromRawAmount(rawBalance, decimals);
      const valueUsd = balanceWhole * Number(row.last_price_usd || 0);
      return { ...ui, balance_whole: balanceWhole, value_usd: valueUsd };
    }).filter((h) => h.value_usd >= 0.5);
  }, [data.holdings, onChain, onChainChecked]);

  // Connected wallet but no URL → push to /profile/{wallet}
  useEffect(() => {
    if (!urlWallet && connectedWallet) {
      navigate(`/profile/${connectedWallet}`, { replace: true });
    }
  }, [urlWallet, connectedWallet, navigate]);

  if (!owner) {
    return (
      <div className="pm-detail">
        <div className="pm-profile__connect">
          <img
            src="/images/png/slurpface (1).png"
            alt="Slurp"
            className="pm-profile__connect-face"
          />
          <h2 className="pm-profile__connect-title">My Profile</h2>
          <p className="pm-profile__connect-sub">
            Connect a Solana wallet to view your profile, holdings,
            launches, and trade history.
          </p>
          <div className="pm-profile__connect-cta">
            <WalletMultiButton />
          </div>
        </div>
      </div>
    );
  }

  const display = profile?.username || shortAddr(owner, 4, 4);

  return (
    <div className="pm-detail">
      <div className="pm-profile">
        {xVerifyBanner && (
          <div
            className={`pm-profile__xbanner pm-profile__xbanner--${xVerifyBanner === "ok" ? "ok" : "err"}`}
            role="status"
          >
            <span>
              {xVerifyBanner === "ok"
                ? "X account verified."
                : xVerifyBanner === "x_already_bound"
                ? "That X account is already linked to another wallet."
                : xVerifyBanner === "expired"
                ? "Verification request expired. Try again."
                : `Verification failed (${xVerifyBanner}).`}
            </span>
            <button
              type="button"
              className="pm-profile__xbanner-x"
              onClick={() => setXVerifyBanner(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {/* Identity panel */}
        <div className="pm-profile__identity">
          <button
            type="button"
            className="pm-profile__avatar"
            onClick={async () => {
              await copyToClipboard(owner);
              setCopiedAddr(true);
              setTimeout(() => setCopiedAddr(false), 1400);
            }}
            title="Copy wallet address"
          >
            {profile?.image_url ? (
              <img src={profile.image_url} alt={display} />
            ) : (
              <img
                src="/images/png/slurpface (1).png"
                alt="Slurp"
                className="pm-profile__avatar-default"
              />
            )}
            {/* Bottom strip: wallet shortAddr (or "Copied" flash on click) */}
            <div className="pm-profile__avatar-name" title={owner}>
              {copiedAddr ? "Copied" : shortAddr(owner, 4, 4)}
            </div>
          </button>

          <div className="pm-profile__id-col">
            <div className="pm-profile__id-head">
              <h1 className="pm-profile__name">
                {profile?.username ? `@${profile.username}` : shortAddr(owner, 4, 4)}
              </h1>
              <div className="pm-profile__socials">
                {profile?.twitter && (
                  <a
                    className={`pm-profile__social${profile?.x_verified_username ? " is-verified" : ""}`}
                    href={profile.twitter}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={profile?.x_verified_username ? `X verified by @${profile.x_verified_username}` : "X"}
                    data-tip={profile?.x_verified_username ? `X verified by @${profile.x_verified_username}` : undefined}
                  >
                    <IconX />
                    {profile?.x_verified_username && (
                      <BadgeCheck
                        className="pm-profile__social-check"
                        aria-hidden="true"
                        size={16}
                        strokeWidth={2.5}
                      />
                    )}
                  </a>
                )}
                {profile?.website && (
                  <a className="pm-profile__social" href={profile.website} target="_blank" rel="noreferrer" aria-label="Website">
                    <IconGlobe />
                  </a>
                )}
                {profile?.telegram && (
                  <a className="pm-profile__social" href={profile.telegram} target="_blank" rel="noreferrer" aria-label="Telegram">
                    <IconTelegram />
                  </a>
                )}
                {profile?.github && (
                  <a className="pm-profile__social" href={profile.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                    <IconGithub />
                  </a>
                )}
              </div>
            </div>
            {profile?.bio && (
              <div className="pm-profile__bio-sticker">
                <span className="pm-profile__bio">{profile.bio}</span>
              </div>
            )}

            {/* Follower / following stat row — clickable to see who */}
            <div className="pm-profile__followstats">
              <button
                type="button"
                className="pm-profile__followstat"
                onClick={() => openFollowList("followers")}
                disabled={followStats.followers === 0}
                title="Show followers"
              >
                <b>{followStats.followers.toLocaleString()}</b>
                <span>follower{followStats.followers === 1 ? "" : "s"}</span>
              </button>
              <button
                type="button"
                className="pm-profile__followstat"
                onClick={() => openFollowList("following")}
                disabled={followStats.following === 0}
                title="Show following"
              >
                <b>{followStats.following.toLocaleString()}</b>
                <span>following</span>
              </button>
            </div>
          </div>

          <div className="pm-profile__cta-col">
            {isSelf ? (
              <button
                className="pm-profile__edit"
                onClick={() => setEditOpen(true)}
                title={profile ? "Edit profile" : "Set up profile"}
              >
                <Pencil size={14} />
                <span>{profile ? "Edit" : "Set up"}</span>
              </button>
            ) : connectedWallet ? (
              <button
                className={
                  "pm-profile__follow" +
                  (followStats.viewer_follows ? " pm-profile__follow--following" : "")
                }
                onClick={toggleFollow}
                disabled={followBusy}
                title={followStats.viewer_follows ? "Unfollow" : "Follow"}
              >
                {followStats.viewer_follows ? <UserCheck size={14} /> : <UserPlus size={14} />}
                <span>
                  {followBusy
                    ? "…"
                    : followStats.viewer_follows
                      ? "Following"
                      : "Follow"}
                </span>
              </button>
            ) : null}
          </div>
        </div>

        {/* User search — find any profile by username or wallet */}
        <div className="pm-profile__search">
          <Search className="pm-profile__search-icon" size={16} aria-hidden="true" />
          <input
            type="search"
            className="pm-profile__search-input"
            placeholder="Search users by @username or wallet…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            // Delay the close so a click on a result fires before blur
            // collapses the dropdown.
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
          />
          {searchOpen && searchQuery.trim().length >= 2 && (
            <div className="pm-profile__search-drop">
              {searchLoading && searchResults.length === 0 ? (
                <div className="pm-profile__search-empty">Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className="pm-profile__search-empty">No matches.</div>
              ) : (
                <ul className="pm-profile__search-list">
                  {searchResults.map((r) => (
                    <li key={r.wallet}>
                      <Link
                        to={`/profile/${r.wallet}`}
                        className="pm-profile__search-row"
                        onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                      >
                        <span className="pm-profile__search-avatar" aria-hidden="true">
                          {r.image_url ? (
                            <img src={r.image_url} alt="" />
                          ) : (
                            <span className="pm-profile__search-avatar-fallback">
                              {(r.username || r.wallet).slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="pm-profile__search-name">
                          {r.username ? `@${r.username}` : shortAddr(r.wallet, 4, 4)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="pm-tabs">
          <button data-active={tab === "launched"} onClick={() => setTab("launched")}>
            Launched <span className="pm-tabs__count">{launches.length}</span>
          </button>
          <button
            data-active={tab === "holdings"}
            onClick={() => { setTab("holdings"); setHoldingsRequested(true); }}
          >
            Holdings
          </button>
          <button data-active={tab === "trades"} onClick={() => setTab("trades")}>
            Trade history
          </button>
        </div>

        {fetchError && !loading && (
          <div className="pm-empty" style={{ padding: 18, color: "var(--pm-down)" }}>
            Couldn't load: {fetchError}
          </div>
        )}

        {/* Tab content */}
        {tab === "holdings" && (
          loading || holdings === null ? <SlurpLoader /> :
          holdings.length === 0 ? (
            <div className="pm-empty" style={{ padding: 18 }}>
              {isSelf ? "No holdings yet, buy some Slurp tokens to fill this up." : "No holdings."}
            </div>
          ) : (
            <div className="pm-holdings">
              {holdings.map((h) => (
                <Link key={h.contractAddress} to={`/token/${h.contractAddress}`} className="pm-holding">
                  <div className="pm-holding__image">
                    {h.image?.url ? (
                      <img src={h.image.url} alt={h.name} />
                    ) : (
                      <div className="pm-holding__image-ph" />
                    )}
                    <span className="pm-holding__name-sticker">${h.symbol || "?"}</span>
                  </div>
                  <div className="pm-holding__body">
                    <div className="pm-holding__title-row">
                      <span className="pm-holding__name">{h.name}</span>
                    </div>
                    <div className="pm-holding__value-sticker">{fmtUsd(h.value_usd)}</div>
                    <div className="pm-holding__stats">
                      <div className="pm-holding__stat">
                        <span className="pm-holding__stat-label">Balance</span>
                        <span className="pm-holding__stat-value">
                          {h.balance_whole.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="pm-holding__stat">
                        <span className="pm-holding__stat-label">Price</span>
                        <span className="pm-holding__stat-value">{fmtUsd(h.last_price_usd)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}

        {tab === "launched" && (
          loading ? <SlurpLoader /> :
          launches.length === 0 ? (
            <div className="pm-empty" style={{ padding: 18 }}>
              {isSelf ? "You haven't launched anything yet." : "No launches."}
            </div>
          ) : (
            <div className="pm-grid">
              {launches.map(({ raw, adapted }) => (
                <LaunchCardWithClaim
                  key={adapted.contractAddress}
                  rawToken={raw}
                  adapted={adapted}
                  viewerIsOwner={isSelf}
                  prefetchedPendingLamports={pendingFees[raw.mint]?.sol_lamports}
                />
              ))}
            </div>
          )
        )}

        {tab === "trades" && (
          tradesLoading ? <SlurpLoader /> :
          trades.length === 0 ? (
            <div className="pm-empty" style={{ padding: 18 }}>
              No trades yet.
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Token</th>
                    <th>Side</th>
                    <th className="right">SOL</th>
                    <th className="right">USD</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t) => {
                    const sol = Number(t.sol_raw || 0) / 1e9;
                    const usd = sol * Number(t.sol_usd || 0);
                    return (
                      <tr
                        key={t.signature}
                        className="pm-table__row pm-table__row--link"
                        onClick={() => window.open(`https://solscan.io/tx/${t.signature}`, "_blank", "noopener,noreferrer")}
                        title="View transaction on Solscan"
                      >
                        <td className="dim">{timeAgo(new Date(t.ts).getTime())}</td>
                        <td>
                          <Link
                            to={`/token/${t.mint}`}
                            onClick={(e) => e.stopPropagation()}
                            className="pm-profile-trade__token"
                          >
                            {t.token_image_url && (
                              <img src={t.token_image_url} alt="" />
                            )}
                            <span>${t.token_symbol || "?"}</span>
                          </Link>
                        </td>
                        <td className={t.side === "buy" ? "up" : "down"}>
                          {t.side === "buy" ? "Buy" : "Sell"}
                        </td>
                        <td className="right">{sol.toFixed(4)}</td>
                        <td className="right">{fmtUsd(usd)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* The trades table is capped at 90 days by the indexer's
                  retention sweep. Without this note a user who's been
                  trading for months wonders why their old trades are
                  missing — directing them to Solscan answers it. */}
              <div className="pm-table-foot">
                Showing last 90 days ·{" "}
                <a
                  href={`https://solscan.io/account/${owner}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  full history on Solscan
                </a>
              </div>
            </div>
          )
        )}
      </div>

      {editOpen && (
        <ProfileFormModal
          mode="edit"
          initial={profile}
          onClose={() => setEditOpen(false)}
          onSaved={(p) => { setProfile(p); setEditOpen(false); }}
        />
      )}

      {followListMode && (
        <div
          className="pm-modal__bg"
          onClick={() => setFollowListMode(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="pm-modal pm-followlist" onClick={(e) => e.stopPropagation()}>
            <button
              className="pm-modal__close"
              onClick={() => setFollowListMode(null)}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            <h3 className="pm-modal__title">
              {followListMode === "followers" ? "Followers" : "Following"}
            </h3>
            {followListLoading ? (
              <SlurpLoader />
            ) : followListData.length === 0 ? (
              <div className="pm-empty" style={{ padding: 18 }}>
                {followListMode === "followers" ? "No followers yet." : "Not following anyone yet."}
              </div>
            ) : (
              <div className="pm-followlist__items">
                {followListData.map((row) => (
                  <Link
                    key={row.wallet}
                    to={`/profile/${row.wallet}`}
                    className="pm-followlist__row"
                    onClick={() => setFollowListMode(null)}
                  >
                    <div
                      className="pm-followlist__avatar"
                      style={!row.image_url ? { background: gradientFor(row.wallet) } : undefined}
                    >
                      {row.image_url
                        ? <img src={row.image_url} alt="" />
                        : <img src="/images/png/slurpface (1).png" alt="" />
                      }
                    </div>
                    <div className="pm-followlist__text">
                      {row.username ? (
                        <span className="pm-followlist__sticker">@{row.username}</span>
                      ) : (
                        <span className="pm-followlist__name">{shortAddr(row.wallet)}</span>
                      )}
                      {row.username && (
                        <span className="pm-followlist__addr">{shortAddr(row.wallet)}</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
